import { DeploymentData, DeploymentType } from "types/deploymentTypes";
import { InvoiceData } from "types/invoiceTypes";
import { ValidationResult } from "types/types";
import { isValidEmail } from "./Utils";


export const validateDeploymentData = (
    data: DeploymentData,
    type: DeploymentType
): ValidationResult => {
    const errors: DeploymentData["errors"] = {};

    if (!data.region?.value) errors.region = "Please select a region";

    if (!data.name?.trim()) errors.name = "Please enter a name";
    else if (/\s/.test(data.name)) errors.name = "The name should not contain whitespace";
    else if (/-$/.test(data.name)) errors.name = "The name must not end with a hyphen";
    else if (data.name.includes("--")) errors.name = "The name must not contain consecutive hyphens";
    else if (data.name.length > 30) errors.name = "The name has too many characters";

    if (!data.organizationId?.value) errors.organizationId = "Please select an organization";

    if (type === "dedicated") {
        if (!data.domainName?.trim()) {
            errors.domainName = "Please enter a domain name";
        } else if (data.domainName.length > 50) {
            errors.domainName = "The domain name has too many characters";
        }else if (/-$/.test(data.domainName)){
            errors.domainName = "The domain name must not end with a hyphen";
        }

        if (!data.deploymentPlan) {
            errors.deploymentPlan = "Please select a deployment plan";
        } else if (!["develop", "scale", "accelerate"].includes(data.deploymentPlan.toLowerCase())) {
            errors.deploymentPlan = "Invalid deployment plan selected";
        }

        if (!data.billingCycle) {
            errors.billingCycle = "Please select a billing cycle";
        } else if (!["monthly", "yearly"].includes(data.billingCycle.toLowerCase())) {
            errors.billingCycle = "Invalid billing cycle selected";
        }

        if(!data.keycloakVersion){
          errors.keycloakVersion = "Please enter a keycloak version";
        }
        
        if(!data.clusterSize){
          errors.clusterSize = "Please select a cluster size";
        }
    }

    if (Object.keys(errors).length > 0) return { isValid: false, errors };

    const validData: Record<string, any> = {
        region: data.region?.value ?? "",
        name: data.name.trim(),
        organizationId: data.organizationId?.value ?? "",
    };

    // ✅ Add dedicated-specific fields when needed
    if (type === "dedicated") {
        validData.domainName = data.domainName?.trim() ?? "";
        validData.hostedZoneId = data.hostedZoneId;
        validData.deploymentPlan = data.deploymentPlan?.toLowerCase() ?? "";
        validData.billingCycle = data.billingCycle?.toLowerCase() ?? "";
        validData.subscriptionId = "a24095e7-5203-4eda-b282-3a4a55b62a81"; // optional placeholder
        validData.keycloakVersion = data.keycloakVersion?.value ?? "";
        validData.clusterSize = data.clusterSize ?? "";
        validData.useAlb = data.useAlb;
    }

    return { isValid: true, validData };
};

export const  validateDomain = (value: string) => {
  const trimmed = value.trim();

  // Empty → silent invalid
  if (trimmed.length === 0) {
    return { isValid: false, error: null };
  }

  // Total length
  if (trimmed.length > 50) {
    return {
      isValid: false,
      error: "Domain must not exceed 50 characters.",
    };
  }

  // Allowed characters: letters, numbers, hyphens, dots
  if (!/^[a-zA-Z0-9.-]+$/.test(trimmed)) {
    return {
      isValid: false,
      error: "Domain can only contain letters, numbers, hyphens, and dots.",
    };
  }

  // Cannot start or end with hyphen
  if (trimmed.startsWith("-") || trimmed.endsWith("-")) {
    return {
      isValid: false,
      error: "Domain cannot start or end with a hyphen.",
    };
  }
  
  // Cannot start or end with dot
  if (trimmed.startsWith(".") || trimmed.endsWith(".")) {
    return {
      isValid: false,
      error: "Domain cannot start or end with a dot.",
    };
  }

  // ✅ All good
  return { isValid: true, error: null };
}

export const validateInvoiceData = (data:InvoiceData): ValidationResult=>{
  const errors:InvoiceData["errors"] = {};

  if(!data.firstName) errors.firstName = "Please enter a first name";
  if(!data.lastName) errors.lastName = "Please enter a last name";

  if(!data.billingEmail) errors.billingEmail = "Please enter a billing email";
  else if(!isValidEmail(data.billingEmail)) errors.billingEmail = "Please enter a valid email address.";

  if(!data.phoneNumber) errors.phoneNumber = "Please enter a phone number";
  if(!data.company) errors.company = "Please enter a company";
  if(!data.billingAddress) errors.billingAddress = "Please enter a billing address";
  if(!data.city) errors.city = "Please enter a city";
  if(!data.postalCode) errors.postalCode = "Please enter a postal code";

  if (Object.keys(errors).length > 0) return { isValid: false, errors };

  return { isValid: true, validData:data };
}